import React, { useState, useEffect } from 'react';
import { Search, Plus, Filter, Trash, AlertCircle, FileUp } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jobService } from '../../../services/job.service';
import { offerLetterApi } from '../../../services/offerLetter.service';
import ResumeUpload from '../resume/ResumeUpload';
import { toast } from 'react-toastify';


const JobsTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('All Jobs');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [deletingJob, setDeletingJob] = useState(false);
  const [totalResumes, setTotalResumes] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [uploadTemplateJob, setUploadTemplateJob] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  const tabs = ['All Jobs', 'Completed', 'Incomplete', 'In-Progress'];
  const ITEMS_PER_PAGE = 10;

  const fetchJobs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const filters = {};
      if (activeTab !== 'All Jobs') {
        filters.status = activeTab;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }

      const response = await jobService.getJobs({
        ...filters,
        page: currentPage,
        limit: ITEMS_PER_PAGE
      });

      if (response.data) {
        setJobs(response.data.jobs || []);
        setTotalResumes(response.data.pagination?.total || 0);
        setTotalPages(Math.ceil((response.data.pagination?.total || 0) / ITEMS_PER_PAGE));
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [activeTab, searchQuery, currentPage]);

  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'Incomplete': return 'bg-orange-100 text-orange-700';
      case 'In-Progress': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleRowClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  const handleCreateJob = () => {
    navigate('/jobs/create');
  };

  const openDeleteModal = (job, e) => {
    e.stopPropagation();
    setJobToDelete(job);
    setDeleteModalOpen(true);
  };

  const handleTemplateUpload = async (files) => {
    if (!uploadTemplateJob) return;
    try {
      setUploadingTemplate(true);
      const formData = new FormData();
      formData.append('template', files[0]);
      formData.append('jobId', uploadTemplateJob._id);
      await offerLetterApi.uploadTemplate(uploadTemplateJob._id, formData);
      toast.success(`Template uploaded for "${uploadTemplateJob.title}"`);
      setUploadTemplateJob(null);
    } catch (err) {
      toast.error('Failed to upload template');
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!jobToDelete) return;

    setDeletingJob(true);
    try {
      await jobService.deleteJob(jobToDelete._id);
      setJobs(jobs.filter(job => job._id !== jobToDelete._id));
      setDeleteModalOpen(false);
    } catch (err) {
      setError('Failed to delete job. Please try again.');
    } finally {
      setDeletingJob(false);
      setJobToDelete(null);
    }
  };

  

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Jobs</h1>
        <button 
          onClick={handleCreateJob}
          className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-sm"
        >
          <Plus className="w-5 h-5" />
          Create New JD
        </button>
      </div>

      <div className="flex border-b mb-6">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`px-4 py-2 -mb-px transition-colors relative ${
              activeTab === tab
                ? 'text-orange-500 border-b-2 border-orange-500 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
            {activeTab === tab && (
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-orange-500 to-orange-600"></span>
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="relative flex-1 max-w-xl">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Job ID, Title, or Company Name"
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
          />
        </div>
        {/* <button className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors">
          <Filter className="w-5 h-5" />
          Filter
        </button> */}
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">No.</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Job ID</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Job Title</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Company Name</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Total Candidates</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Template</th>
              <th className="w-12 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                    Loading jobs...
                  </div>
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No jobs found
                </td>
              </tr>
            ) : (
              jobs.map((job, index) => (
                <tr 
                  key={job._id} 
                  onClick={() => handleRowClick(job._id)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-gray-500">
                    {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                  </td>
                  <td className="px-4 py-3 text-orange-500 font-medium">{job.jobId}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{job.title}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {job.companyId?.name || 'No Company Assigned'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center">
                      <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span>{job.statistics?.totalCandidates || 0}</span>
                      {/* {job.statistics?.totalCandidates > 0 && (
                        // <span className="text-sm text-gray-500">
                        //   ({job.statistics?.shortlisted || 0} shortlisted)
                        // </span>
                      )} */}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setUploadTemplateJob(job); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-lg text-xs font-semibold hover:bg-orange-100 transition-colors mx-auto"
                    >
                      <FileUp className="w-3.5 h-3.5" />
                      {job.offerLetterTemplate ? 'Update' : 'Upload'}
                    </button>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => openDeleteModal(job, e)}>
                    <button 
                      onClick={(e) => openDeleteModal(job, e)}
                      className="p-2 hover:bg-red-50 rounded-full transition-colors group"
                      aria-label={`Delete job ${job.title}`}
                      title="Delete job"
                    >
                      <Trash className="w-5 h-5 text-gray-400 group-hover:text-red-500" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Pagination Controls */}
      {!isLoading && jobs.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow-sm">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalResumes)} of {totalResumes} entries
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // Show current page, first page, last page, and pages around current page
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`px-3 py-1 border rounded-md ${
                      currentPage === pageNumber
                        ? 'bg-orange-500 text-white'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber}>...</span>;
              }
              return null;
            })}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {uploadTemplateJob && (
        <ResumeUpload
          isOpen={!!uploadTemplateJob}
          onClose={() => setUploadTemplateJob(null)}
          onUpload={handleTemplateUpload}
          title="offerLetter"
          accept=".doc,.docx,.pdf"
          maxFiles={1}
          loading={uploadingTemplate}
        />
      )}

      {deleteModalOpen && (
  <div className="fixed inset-0 z-50 overflow-y-auto">
    {/* Overlay */}
    <div 
      className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
      onClick={() => !deletingJob && setDeleteModalOpen(false)}
    ></div>

    {/* Modal */}
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
        {/* Close button */}
        {!deletingJob && (
          <button 
            onClick={() => setDeleteModalOpen(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Content */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Delete Job
          </h3>
          <p className="text-sm text-gray-500">
            Are you sure you want to delete job "{jobToDelete?.title}"? This action cannot be undone.
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setDeleteModalOpen(false)}
            disabled={deletingJob}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteJob}
            disabled={deletingJob}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {deletingJob && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-b-transparent"></div>
            )}
            {deletingJob ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default JobsTable;